// Pre-launch screen: shown on the booking entry until RESERVATION_OPENS_AT.
// A live countdown to the opening; flips to the booking flow automatically the
// moment it reaches zero (calls onOpen). The server also rejects holds/
// reservations before the same instant, so this is presentation, not the gate.

import { SiteHeader } from '@/components/SiteHeader.tsx';
import { RESERVATION_OPENS_AT, trDot } from '@okn/shared';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';

export function useCountdownTo(targetIso: string) {
  const target = useMemo(() => new Date(targetIso).getTime(), [targetIso]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const remaining = Math.max(0, target - now);
  const sec = Math.floor(remaining / 1000);
  return {
    done: remaining <= 0,
    d: Math.floor(sec / 86400),
    h: Math.floor((sec % 86400) / 3600),
    m: Math.floor((sec % 3600) / 60),
    s: sec % 60,
  };
}

export function formatOpensAt(): string {
  try {
    return trDot(
      new Intl.DateTimeFormat('tr-TR', {
        timeZone: 'Europe/Istanbul',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(RESERVATION_OPENS_AT)),
    );
  } catch {
    return '13 Haziran Cumartesi 17.00';
  }
}

export function ReservationOpening({ onOpen }: { onOpen?: () => void }) {
  const { d, h, m, s, done } = useCountdownTo(RESERVATION_OPENS_AT);

  useEffect(() => {
    if (done) onOpen?.();
  }, [done, onOpen]);

  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader />
      <main className="relative mx-auto flex w-full max-w-4xl flex-1 flex-col items-center justify-center px-8 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="kicker mb-5 text-accent">
            <span className="mr-3 inline-block h-px w-10 align-middle bg-accent" />
            Rezervasyon yakında
          </div>
          <h1 className="display text-[clamp(30px,5.2vw,64px)] font-light leading-[1.04] tracking-tight">
            Rezervasyon
            <br />
            <em className="italic text-accent">{formatOpensAt()}</em>'de açılıyor
          </h1>
          <p className="mx-auto mt-6 max-w-md text-[15px] leading-relaxed text-text-muted">
            Koltuk seçimi açılış anında başlar. Bu sayfa, açıldığında otomatik olarak rezervasyona
            döner — yenilemenize gerek yok.
          </p>

          <div className="mt-12 grid grid-cols-4 gap-3 sm:gap-5">
            <Unit n={d} label="gün" />
            <Unit n={h} label="saat" />
            <Unit n={m} label="dakika" />
            <Unit n={s} label="saniye" />
          </div>
        </motion.div>
      </main>
    </div>
  );
}

function Unit({ n, label }: { n: number; label: string }) {
  return (
    <div className="rounded-xl border border-border bg-bg-elevated px-2 py-5 sm:px-4 sm:py-7">
      <div className="display text-[clamp(28px,6vw,56px)] font-light leading-none tnum">
        {n.toString().padStart(2, '0')}
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-text-faint">{label}</div>
    </div>
  );
}
